import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import EvidenceGuidelinesSection from './EvidenceGuidelinesSection';
import AlternativeOptionsSection from './AlternativeOptionsSection';


const AIRecommendationDisplay = ({ 
  recommendation, 
  isLoading, 
  onSave, 
  onCopy,
  onExportPDF
}) => {
  if (isLoading) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 shadow-sm">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="relative w-16 h-16 mb-6">
            <div className="absolute inset-0 border-4 border-primary/20 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            <Icon name="Brain" size={32} color="var(--color-primary)" className="absolute inset-0 m-auto" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Analyzing Clinical Data</h3>
          <p className="text-sm text-muted-foreground text-center max-w-md">
            AI is processing patient information and generating evidence-based imaging recommendations...
          </p>
          <div className="mt-6 w-full max-w-xs">
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full animate-progress" style={{ width: '70%' }}></div>
            </div>
            <p className="text-xs text-muted-foreground text-center mt-2">Estimated completion: 15-30 seconds</p>
          </div>
        </div>
      </div>
    );
  }

  if (!recommendation) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 shadow-sm">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
            <Icon name="FileSearch" size={32} color="var(--color-muted-foreground)" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No Recommendation Yet</h3>
          <p className="text-sm text-muted-foreground text-center max-w-md">
            Complete the clinical presentation form and click "Generate Recommendation" to receive AI-powered imaging guidance.
          </p>
        </div>
      </div>
    );
  }

  const getGradeColor = (grade) => {
    switch(grade) {
      case 'A': return 'bg-success/10 text-success border-success/20';
      case 'B': return 'bg-warning/10 text-warning border-warning/20';
      case 'C': return 'bg-error/10 text-error border-error/20';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getUrgencyColor = (urgency) => {
    switch(urgency) {
      case 'immediate': return 'text-error';
      case 'urgent': return 'text-warning';
      case 'routine': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm">
      <div className="p-6 border-b border-border bg-primary/5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Sparkles" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground mb-1">AI-Generated Recommendation</h2>
              <p className="text-sm text-muted-foreground">Evidence-based imaging guidance with iRefer compliance</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" iconName="Copy" onClick={onCopy}>
              Copy
            </Button>
            <Button variant="outline" size="sm" iconName="Download" onClick={onExportPDF}>
              Export PDF
            </Button>
            <Button variant="default" size="sm" iconName="Save" onClick={onSave}>
              Save
            </Button>
          </div>
        </div>
      </div>
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${getGradeColor(recommendation?.evidenceGrade)}`}>
            <Icon name="Award" size={16} />
            <span className="text-sm font-semibold">Grade {recommendation?.evidenceGrade}</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Clock" size={16} className={getUrgencyColor(recommendation?.urgency)} />
            <span className={`text-sm font-medium capitalize ${getUrgencyColor(recommendation?.urgency)}`}>
              {recommendation?.urgency}
            </span>
          </div>
          {recommendation?.radiationDose && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-warning/10 rounded-lg">
              <Icon name="Zap" size={16} color="var(--color-warning)" />
              <span className="text-sm font-medium text-warning">{recommendation?.radiationDose} mSv</span>
            </div>
          )}
        </div>

        <div>
          <h3 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="Target" size={18} color="var(--color-primary)" />
            Recommended Imaging
          </h3>
          <div className="bg-muted/50 rounded-lg p-4">
            <p className="text-lg font-semibold text-foreground mb-2">{recommendation?.primaryModality}</p>
            <p className="text-sm text-muted-foreground">{recommendation?.modalityRationale}</p>
          </div>
        </div>

        {/* NEW: Alternative Options Section */}
        <AlternativeOptionsSection recommendation={recommendation} />

        <div>
          <h3 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="FileText" size={18} color="var(--color-primary)" />
            Clinical Rationale
          </h3>
          <p className="text-sm text-foreground leading-relaxed">{recommendation?.clinicalRationale}</p>
        </div>

        {recommendation?.contrastRequired && (
          <div className="p-4 bg-accent/10 border border-accent/20 rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="Droplet" size={20} color="var(--color-accent)" />
              <div>
                <h4 className="text-sm font-semibold text-accent mb-1">Contrast Administration Required</h4>
                <p className="text-xs text-muted-foreground">{recommendation?.contrastDetails}</p>
              </div>
            </div>
          </div>
        )}

        {recommendation?.safetyWarnings && recommendation?.safetyWarnings?.length > 0 && (
          <div className="p-4 bg-error/10 border border-error/20 rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="AlertTriangle" size={20} color="var(--color-error)" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-error mb-2">Safety Considerations</h4>
                <ul className="space-y-1">
                  {recommendation?.safetyWarnings?.map((warning, index) => (
                    <li key={index} className="text-xs text-muted-foreground flex items-start gap-2">
                      <span className="text-error mt-0.5">•</span>
                      <span>{warning}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* UPDATED: Evidence & Guidelines Section */}
        {recommendation?.ireferCode ? (
          <EvidenceGuidelinesSection recommendation={recommendation} />
        ) : (
          <div>
            <h3 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="BookOpen" size={18} color="var(--color-primary)" />
              Evidence & Guidelines
            </h3>
            <div className="space-y-2">
              {recommendation?.iReferGuidelines?.map((guideline, index) => (
                <div key={index} className="p-3 bg-muted/30 rounded-lg border border-border">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">{guideline?.title}</p>
                      <p className="text-xs text-muted-foreground mt-1">{guideline?.reference}</p>
                    </div>
                    <a 
                      href={guideline?.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary/80 transition-clinical"
                    >
                      <Icon name="ExternalLink" size={16} />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="pt-4 border-t border-border">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Generated: {new Date()?.toLocaleString()}</span>
            <span>Recommendation ID: {recommendation?.id}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIRecommendationDisplay;